import dayjs from "dayjs";
import { GetObservation } from "@/services/fhir/observation/GetObservation";

const PAGE_SIZE = 350;

/** Formatos de code que o MHD costuma aceitar para passos. */
const STEPS_CODES = ["http://loinc.org|55423-8", "55423-8"] as const;

/** Tempo de tela: some no _count=350 quando há muitos syncs recentes. */
const SCREEN_TIME_CODES = ["screen-time"] as const;

/**
 * Dados gerais (e afins) em busca dedicada — mesmo problema do tempo de tela:
 * sync mais antigo some do _count=350 e o gráfico fica com poucos dias.
 */
const GENERAL_METRIC_CODES = [
  "http://loinc.org|8867-4",
  "8867-4", // Frequência cardíaca
  "http://loinc.org|85354-9",
  "85354-9", // Pressão arterial
  "http://loinc.org|9052-2",
  "9052-2", // Calorias
  "http://loinc.org|8999-5",
  "8999-5", // Água
  "http://loinc.org|2339-0",
  "2339-0", // Glicose
] as const;

/**
 * Sono / SpO2 / temperatura: começam na noite anterior ao dia do gráfico.
 * Sem busca dedicada + 1 dia de folga, somem do _count=350 e do date=ge.
 */
const SLEEP_METRIC_CODES = [
  "http://loinc.org|93832-4",
  "93832-4", // Sono total
  "http://loinc.org|93830-8",
  "93830-8", // Sono leve
  "http://loinc.org|93831-6",
  "93831-6", // Sono profundo
  "http://loinc.org|93829-0",
  "93829-0", // Sono REM
  "http://loinc.org|93828-2",
  "93828-2", // Sono acordado
  "http://loinc.org|96634-8",
  "96634-8", // SpO2 abaixo de 90%
  "http://loinc.org|59408-5",
  "59408-5", // SpO2
  "http://loinc.org|81264-4",
  "81264-4", // Temperatura corporal
] as const;

/** SpO2 tem muitas Observations por noite — fatias menores evitam corte no _count. */
const SPO2_CODES = new Set(["59408-5", "http://loinc.org|59408-5"]);

function mergeEntries(
  bundles: Array<{ entry?: any[] } | null | undefined>
): any[] {
  const seen = new Set<string>();
  const entries: any[] = [];
  for (const bundle of bundles) {
    for (const entry of bundle?.entry ?? []) {
      const id = String(entry?.resource?.id || entry?.fullUrl || "");
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      entries.push(entry);
    }
  }
  return entries;
}

/** Particiona [startKey, endKey] em janelas de até chunkDays (inclusive). */
function chunkDateRange(
  startKey: string,
  endKey: string,
  chunkDays: number
): Array<{ start: string; end: string }> {
  const start = dayjs(startKey);
  const end = dayjs(endKey);
  if (!start.isValid() || !end.isValid() || start.isAfter(end, "day")) {
    return [{ start: startKey, end: endKey }];
  }

  const windows: Array<{ start: string; end: string }> = [];
  let cursor = start;
  while (cursor.isBefore(end, "day") || cursor.isSame(end, "day")) {
    const windowEnd = cursor.add(chunkDays - 1, "day");
    const capped = windowEnd.isAfter(end, "day") ? end : windowEnd;
    windows.push({
      start: cursor.format("YYYY-MM-DD"),
      end: capped.format("YYYY-MM-DD"),
    });
    cursor = capped.add(1, "day");
  }
  return windows.length > 0 ? windows : [{ start: startKey, end: endKey }];
}

/**
 * Busca Observations do período.
 * Passos, tela, dados gerais e sono em busca dedicada (não competem no _count=350).
 */
export async function fetchWearableObservationEntries(params: {
  patientId: string;
  periodStartKey: string;
  periodEndKey: string;
  rangeDays: number;
}): Promise<any[]> {
  const { patientId, periodStartKey, periodEndKey, rangeDays } = params;

  const fetchWindow = (startDate: string, endDate: string, code?: string) =>
    GetObservation({
      patientId,
      count: PAGE_SIZE,
      startDate,
      endDate,
      lastUpdaded: true,
      ...(code ? { code } : {}),
    });

  const dedicatedCodes = [
    ...STEPS_CODES,
    ...SCREEN_TIME_CODES,
    ...GENERAL_METRIC_CODES,
  ];

  const dedicatedPromise = Promise.all(
    dedicatedCodes.map((code) =>
      fetchWindow(periodStartKey, periodEndKey, code).catch(() => null)
    )
  );

  // Sono: recua 1 dia (noite anterior) e fatia a busca —
  // estágios/temp em 15d; SpO2 em 7d (volume alto + syncs antigos).
  const sleepStartKey = dayjs(periodStartKey)
    .subtract(1, "day")
    .format("YYYY-MM-DD");

  const sleepPromise = Promise.all(
    SLEEP_METRIC_CODES.flatMap((code) => {
      const chunkDays = SPO2_CODES.has(code) ? 7 : 15;
      return chunkDateRange(sleepStartKey, periodEndKey, chunkDays).map(
        ({ start, end }) =>
          fetchWindow(start, end, code).catch(() => null)
      );
    })
  );

  if (rangeDays <= 15) {
    const [general, dedicatedBundles, sleepBundles] = await Promise.all([
      fetchWindow(periodStartKey, periodEndKey),
      dedicatedPromise,
      sleepPromise,
    ]);
    return mergeEntries([general, ...dedicatedBundles, ...sleepBundles]);
  }

  // 30 dias: 2×15 + buscas dedicadas do período inteiro
  const recentStart = dayjs(periodEndKey)
    .subtract(14, "day")
    .format("YYYY-MM-DD");
  const olderEnd = dayjs(recentStart).subtract(1, "day").format("YYYY-MM-DD");

  const [recent, older, dedicatedBundles, sleepBundles] = await Promise.all([
    fetchWindow(recentStart, periodEndKey),
    fetchWindow(periodStartKey, olderEnd),
    dedicatedPromise,
    sleepPromise,
  ]);

  return mergeEntries([
    recent,
    older,
    ...dedicatedBundles,
    ...sleepBundles,
  ]);
}
